import { useEffect } from 'react'

import { Spinner, Stack } from '@chakra-ui/react'
import type { NextPageWithLayout } from 'next'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'

import { Route } from 'constants/routes'
import {
  useAcceptInviteToBrandMutation,
  useAcceptInviteToVenueMutation,
  UserType,
} from 'generated/generated-graphql'
import { useAuth } from 'providers/UserProvider'
import { useSuccessToast } from 'utils/toast'

export const withRedirect =
  () => (Component: NextPageWithLayout, handleInvitationLinks?: boolean) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const WrappedComponent = (props: any) => {
      const { t } = useTranslation('common')
      const {
        userData,
        isFirebaseInitializing,
        refetchAndSetUserData,
        isLoading,
      } = useAuth()
      const router = useRouter()
      const toast = useSuccessToast()
      const [acceptInviteToBrand] = useAcceptInviteToBrandMutation()
      const [acceptInviteToVenue] = useAcceptInviteToVenueMutation()

      const { invitation } = router.query

      useEffect(() => {
        ;(async () => {
          if (
            !userData ||
            isLoading ||
            !router.isReady ||
            isFirebaseInitializing
          ) {
            return
          }

          if (invitation && handleInvitationLinks) {
            let response
            if (userData.userType === UserType.Brand) {
              response = await acceptInviteToBrand({
                variables: {
                  invitationId: invitation as string,
                },
              })
            } else {
              response = await acceptInviteToVenue({
                variables: {
                  invitationId: invitation as string,
                },
              })
            }

            if (!response?.errors?.length) {
              refetchAndSetUserData()
              // Remove query param from URL
              router.replace(router.pathname, undefined, {
                shallow: true,
              })
              toast({
                title: t('InvitationLinkToast.successToast'),
              })
            }
          }

          if (router.query?.returnTo) {
            router.replace(String(router.query?.returnTo))
          } else {
            router.replace(Route.ChooseYourCompany())
          }
        })()
      }, [
        router,
        userData,
        acceptInviteToBrand,
        invitation,
        acceptInviteToVenue,
        toast,
        t,
        refetchAndSetUserData,
        isLoading,
        isFirebaseInitializing,
      ])

      if (isFirebaseInitializing || userData) {
        return (
          <Stack height="100vh" justify="center" align="center">
            <Spinner size="xl" />
          </Stack>
        )
      }
      return <Component {...props} />
    }
    WrappedComponent.getLayout = Component.getLayout
    return WrappedComponent
  }
